/**
 * Canteen ledger helpers: payment date vs service date, advance payments, reporting.
 */

/** @param {unknown} raw */
export function normalizeCanteenRecordDateString(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const head = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (head) return head[1];
    const iso = raw.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (iso) return iso[1];
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** @param {unknown} status */
export function normalizeCanteenPaymentStatus(status) {
  const s = String(status ?? '').trim().toLowerCase();
  if (s === 'paid') return 'Paid';
  if (s === 'unpaid') return 'Unpaid';
  if (s === 'advance') return 'Advance';
  return status ? String(status) : 'Unpaid';
}

/** @param {object} entry */
export function getCanteenEntryPaymentDate(entry) {
  const raw = entry?.paymentDate ?? entry?.paidDate ?? entry?.date;
  return normalizeCanteenRecordDateString(raw);
}

/** @param {object} entry */
export function getCanteenEntryServiceDate(entry) {
  return normalizeCanteenRecordDateString(entry?.date);
}

export function canteenEntryIsPaid(entry) {
  const st = String(entry?.status ?? '').trim().toLowerCase();
  if (st === 'paid' || st === 'advance') return true;
  if (st === 'unpaid' || st === 'pending' || st === 'owe' || st === 'debt') return false;
  const amount = parseFloat(entry?.amount) || 0;
  const paidPart = parseFloat(entry?.paid);
  if (amount <= 0) return false;
  if (!Number.isNaN(paidPart) && paidPart + 1e-9 >= amount) return true;
  return false;
}

/** Paid before the service/meal date (deferred revenue until service day). */
export function canteenEntryIsAdvance(entry) {
  const st = String(entry?.status ?? '').trim().toLowerCase();
  if (st === 'advance') return canteenEntryIsPaid(entry);
  if (!canteenEntryIsPaid(entry)) return false;
  const serviceDate = getCanteenEntryServiceDate(entry);
  const paymentDate = getCanteenEntryPaymentDate(entry);
  if (!serviceDate || !paymentDate) return false;
  return paymentDate < serviceDate;
}

/**
 * @param {string} serviceDate YYYY-MM-DD
 * @param {string} paymentDate YYYY-MM-DD
 */
export function resolveCanteenStatusForPayment(serviceDate, paymentDate) {
  if (paymentDate && serviceDate && paymentDate < serviceDate) return 'Advance';
  return 'Paid';
}

/**
 * @param {string} dateStr YYYY-MM-DD
 * @param {number} days number of consecutive days (>= 1)
 * @returns {string[]}
 */
export function addConsecutiveDates(dateStr, days) {
  const n = Math.max(1, Math.floor(days) || 1);
  const out = [];
  const [y, m, d] = dateStr.split('-').map(Number);
  const cur = new Date(y, m - 1, d);
  for (let i = 0; i < n; i++) {
    const yy = cur.getFullYear();
    const mo = String(cur.getMonth() + 1).padStart(2, '0');
    const dd = String(cur.getDate()).padStart(2, '0');
    out.push(`${yy}-${mo}-${dd}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** @param {string} startStr YYYY-MM-DD @param {string} endStr YYYY-MM-DD */
export function listDatesInclusive(startStr, endStr) {
  const start = normalizeCanteenRecordDateString(startStr);
  const end = normalizeCanteenRecordDateString(endStr);
  if (!start) return [];
  if (!end || end < start) return [start];
  const out = [];
  const [y, m, d] = start.split('-').map(Number);
  const cur = new Date(y, m - 1, d);
  const [ey, em, ed] = end.split('-').map(Number);
  const endTime = new Date(ey, em - 1, ed).getTime();
  while (cur.getTime() <= endTime) {
    const yy = cur.getFullYear();
    const mo = String(cur.getMonth() + 1).padStart(2, '0');
    const dd = String(cur.getDate()).padStart(2, '0');
    out.push(`${yy}-${mo}-${dd}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/**
 * @param {object} student
 * @param {string} serviceDate YYYY-MM-DD
 */
/** @param {string} status */
export function isSettledCanteenStatus(status) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'paid' || s === 'advance';
}

export function getCanteenLedgerRowForDate(student, serviceDate) {
  const normService = normalizeCanteenRecordDateString(serviceDate) || serviceDate;
  if (!student?.canteen?.length || !normService) {
    return {
      status: 'Unpaid',
      amount: 0,
      paymentDate: null,
      serviceDate: normService,
      isAdvance: false,
      entry: null,
    };
  }
  let entry = null;
  for (const p of student.canteen) {
    if (normalizeCanteenRecordDateString(p?.date) === normService) {
      entry = p;
    }
  }
  if (!entry) {
    return {
      status: 'Unpaid',
      amount: 0,
      paymentDate: null,
      serviceDate: normService,
      isAdvance: false,
      entry: null,
    };
  }
  const rawStatus = entry.status;
  const hasExplicit = String(rawStatus ?? '').trim() !== '';
  let status = hasExplicit
    ? normalizeCanteenPaymentStatus(rawStatus)
    : canteenEntryIsPaid(entry)
      ? 'Paid'
      : 'Unpaid';
  if (status === 'Paid' && canteenEntryIsAdvance(entry)) {
    status = 'Advance';
  }
  if (!canteenEntryIsPaid(entry)) status = 'Unpaid';

  return {
    status,
    amount: parseFloat(entry.amount) || 0,
    paymentDate: getCanteenEntryPaymentDate(entry),
    serviceDate: normService,
    isAdvance: status === 'Advance',
    receiptNumber: entry.receiptNumber,
    entry,
  };
}

/**
 * Settled canteen rows that are advance or future prepaid (editable on payment page).
 * @param {object} student
 * @param {string} [asOfDateStr] YYYY-MM-DD
 */
export function listStudentAdvanceEntries(student, asOfDateStr) {
  const asOf =
    normalizeCanteenRecordDateString(asOfDateStr) ||
    normalizeCanteenRecordDateString(new Date());
  if (!student?.canteen?.length) return [];

  const rows = [];
  for (const entry of student.canteen) {
    if (!canteenEntryIsPaid(entry)) continue;
    const serviceDate = getCanteenEntryServiceDate(entry);
    if (!serviceDate) continue;

    const ledger = getCanteenLedgerRowForDate(student, serviceDate);
    const isAdvance = ledger.isAdvance || canteenEntryIsAdvance(entry);
    const isFuturePrepaid = asOf && serviceDate > asOf;
    if (!isAdvance && !isFuturePrepaid) continue;

    rows.push({
      serviceDate,
      paymentDate: getCanteenEntryPaymentDate(entry),
      amount: parseFloat(entry.amount) || 0,
      status: ledger.status,
      isAdvance,
      description: entry.description || '',
      entry,
    });
  }

  rows.sort((a, b) => a.serviceDate.localeCompare(b.serviceDate));
  return rows;
}

/** Build a single paycanteen row that marks a meal date unpaid. */
export function buildCanteenUnpaidPayloadRow(mongoId, serviceDate, amount = 0) {
  const mealDate =
    normalizeCanteenRecordDateString(serviceDate) || serviceDate;
  return {
    id: mongoId,
    data: {
      canteen: {
        date: mealDate,
        amount: parseFloat(amount) || 0,
        paymentDate: null,
        paid: 0,
        description: `Canteen marked unpaid for ${mealDate}`,
        status: 'Unpaid',
        addedBy: 'admin',
        timestamp: Date.now(),
      },
    },
  };
}

/** Future service dates already paid in advance (prepaid, not yet earned). */
export function getStudentAdvancePrepaidTotal(student, asOfDateStr) {
  const asOf = asOfDateStr || normalizeCanteenRecordDateString(new Date());
  if (!student?.canteen?.length || !asOf) return 0;
  let total = 0;
  for (const entry of student.canteen) {
    if (!canteenEntryIsPaid(entry)) continue;
    const serviceDate = getCanteenEntryServiceDate(entry);
    if (!serviceDate || serviceDate <= asOf) continue;
    total += parseFloat(entry.amount) || 0;
  }
  return total;
}

/**
 * Build paycanteen payload row(s) for one student.
 * @param {{ mongoId: string, amount: number, serviceDates: string[], paymentDate: string, paymentMethod?: string, addedBy?: string }} opts
 */
export function buildCanteenPaymentPayloadRows(opts) {
  const {
    mongoId,
    amount,
    serviceDates,
    paymentDate,
    paymentMethod = 'cash',
    addedBy = 'admin',
  } = opts;
  const payDate = normalizeCanteenRecordDateString(paymentDate) || paymentDate;

  return serviceDates.map((serviceDate) => {
    const status = resolveCanteenStatusForPayment(serviceDate, payDate);
    const isAdvance = status === 'Advance';
    return {
      id: mongoId,
      data: {
        canteen: {
          date: serviceDate,
          amount: parseFloat(amount) || 0,
          paymentDate: payDate,
          description: isAdvance
            ? `Canteen advance payment (meal ${serviceDate}, paid ${payDate})`
            : `Canteen fee payment for ${serviceDate}`,
          status,
          addedBy,
          timestamp: Date.now(),
          paymentMethod,
        },
      },
    };
  });
}
